import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer/Footer';
import Toast from '../../components/Toast/Toast';
import TwoFactorCard from '../../components/TwoFactor/TwoFactorCard';
import TwoFactorPrompt from '../../components/TwoFactor/TwoFactorPrompt';

import { getSettings, updateSettings } from '../../services/SettingsService';
import logger from '../../utils/logger';

function Settings() {

    const { formatMessage } = useIntl();
    const confirmPassword = useRef('');
    const [settings, setSettings] = useState({});
    const [notification, setNotification] = useState({});
    // 2FA retry: backend returns 401 {twoFactorRequired:true} on key changes
    // when 2FA is on. We prompt for a code and re-send the same payload + totp.
    const [twoFaOpen, setTwoFaOpen] = useState(false);
    const [twoFaError, setTwoFaError] = useState('');

    const t = (id) => formatMessage({ id });

    useEffect(() => {

        getSettings()
            .then(result => setSettings(result))
            .catch(err => {
                logger.error(err.response ? err.response.data : err.message);
                setNotification({ type: 'error', text: err.response ? err.response.data : err.message });
            })
    }, []);

    function onInputChange(event) {
        setSettings(prevState => ({ ...prevState, [event.target.id]: event.target.value }));
    }

    // Tracks which section triggered the save so we can surface the right
    // success toast (e.g. "Keys saved & validated" for the Binance section).
    const [saveContext, setSaveContext] = useState('general');

    // Core save. `totp` is set only on a 2FA retry. On a 401 twoFactorRequired
    // response we open the TOTP prompt instead of surfacing an error.
    function saveSettings(totp, context) {
        const ctx = context || saveContext;
        if (context) setSaveContext(context);
        updateSettings(settings, totp)
            .then(result => {
                setTwoFaOpen(false);
                setTwoFaError('');
                if (result)
                    setNotification({
                        type: 'success',
                        text: ctx === 'keys' ? t('settings.keysSaved') : t('settings.savedSuccessfully')
                    });
                else
                    setNotification({ type: 'error', text: result });
            })
            .catch(err => {
                const data = err.response && err.response.data;
                if (data && data.twoFactorRequired) {
                    setTwoFaOpen(true);
                    setTwoFaError(data.error ? t('settings.twoFaInvalid') : '');
                    return;
                }
                logger.error(err.response ? err.response.data : err.message);
                setNotification({ type: 'error', text: err.response ? err.response.data : err.message });
            });
    }

    function onFormSubmit(event, context) {
        if ((settings.password || confirmPassword.current.value)
            && settings.password !== confirmPassword.current.value)
            return setNotification({ type: 'error', text: t('settings.passwordMismatch') });

        setSaveContext(context || 'general');
        saveSettings(undefined, context || 'general');
    }

    return (
        <React.Fragment>
            <Menu />
            <main className="content">
                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                    <div className="d-block mb-4 mb-md-0">
                        <h1 className="h4">{t('settings.title')}</h1>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12">
                        <div className="card card-body border-0 shadow mb-4">
                            <h2 className="h5 mb-4">{t('settings.personal')}</h2>
                            <div className="gh-grid gh-grid-wide mb-3">
                                <div>
                                    <div className="form-group">
                                        <label htmlFor="email">{t('settings.email')}</label>
                                        <input className="form-control" id="email" type="email" placeholder="name@company.com" value={settings.email || ''} onChange={onInputChange} />
                                    </div>
                                </div>
                                <div>
                                    <div className="form-group">
                                        <label htmlFor="phone">{t('settings.phone')}</label>
                                        <input className="form-control" id="phone" type="text" placeholder="+1 51 123456789" value={settings.phone || ''} onChange={onInputChange} />
                                    </div>
                                </div>
                            </div>
                            <div className="gh-grid gh-grid-wide mb-3">
                                <div>
                                    <div>
                                        <label htmlFor="password">{t('settings.newPassword')}</label>
                                        <input className="form-control" id="password" type="password" autoComplete="new-password" placeholder={t('settings.newPasswordPlaceholder')} value={settings.password || ''} onChange={onInputChange} />
                                    </div>
                                </div>
                                <div>
                                    <div>
                                        <label htmlFor="confirmPassword">{t('settings.confirmPassword')}</label>
                                        <input ref={confirmPassword} className="form-control" id="confirmPassword" type="password" autoComplete="new-password" placeholder={t('settings.confirmPasswordPlaceholder')} />
                                    </div>
                                </div>
                            </div>
                            <div className="gh-grid gh-grid-wide mb-3">
                                <div>
                                    <div className="form-group">
                                        <label htmlFor="telegramChat">{t('settings.telegramChatId')}</label><a href={"https://t.me/" + settings.telegramBot} className="badge bg-secondary py-1 ms-1">?</a>
                                        <input className="form-control" id="telegramChat" type="text" placeholder={t('settings.telegramChatIdPlaceholder')} value={settings.telegramChat || ''} onChange={onInputChange} />
                                    </div>
                                </div>
                                <div>
                                    <div className="form-group">
                                        <label htmlFor="limit">{t('settings.limitPlan')}</label>
                                        <input className="form-control" id="limit" type="text" disabled={true} value={settings.limit ? settings.limit.name : ''} />
                                    </div>
                                </div>
                            </div>
                            <div className="d-flex flex-wrap">
                                <button className="btn btn-gray-800 mt-2 animate-up-2" type="button" onClick={onFormSubmit}>{t('settings.save')}</button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12">
                        <div className="card card-body border-0 shadow mb-4">
                            <h2 className="h5 my-4">{t('settings.binance')}</h2>

                            {/* Collapsible "How to get your Binance API keys" helper. */}
                            <div className="mb-3">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    data-bs-toggle="collapse"
                                    data-bs-target="#binanceKeyHelper"
                                    aria-expanded="false"
                                    aria-controls="binanceKeyHelper"
                                >
                                    {t('settings.keyHelperToggle')}
                                </button>
                                <div className="collapse mt-3" id="binanceKeyHelper">
                                    <div className="card card-body bg-gray-100 border-0">
                                        <ol className="mb-2 ps-3">
                                            <li>{t('settings.keyHelperStep1')}</li>
                                            <li>{t('settings.keyHelperStep2')}</li>
                                            <li>{t('settings.keyHelperStep3')}</li>
                                            <li>{t('settings.keyHelperStep4')}</li>
                                        </ol>
                                        <a
                                            href="https://www.binance.com/en/my/settings/api-management"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-sm btn-outline-primary mb-3"
                                        >
                                            {t('settings.keyHelperLink')}
                                        </a>
                                        <p className="small text-success mb-0">
                                            <span className="gh-dot gh-dot-success" />{t('settings.keyHelperWarn')}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-3">
                                <div>
                                    <div className="form-group">
                                        <label htmlFor="accessKey">{t('settings.spotAccessKey')}</label>
                                        <input className="form-control" id="accessKey" type="text" placeholder={t('settings.spotAccessKeyPlaceholder')} value={settings.accessKey || ''} onChange={onInputChange} />
                                    </div>
                                </div>
                            </div>
                            <div className="mb-3">
                                <div>
                                    <div className="form-group">
                                        <label htmlFor="secretKey">{t('settings.newSpotSecretKey')}</label>
                                        <input className="form-control" id="secretKey" type="password" autoComplete="new-password" placeholder={t('settings.newSpotSecretKeyPlaceholder')} value={settings.secretKey || ''} onChange={onInputChange} />
                                    </div>
                                </div>
                            </div>
                            {
                                settings && settings.limit && settings.limit.hasFutures
                                    ? (
                                        <React.Fragment>
                                            <div className="row">
                                                <div className="col-12 mb-3">
                                                    <div className="form-group">
                                                        <label htmlFor="accessKey">{t('settings.futuresAccessKey')}</label>
                                                        <input className="form-control" id="futuresKey" type="text" placeholder={t('settings.futuresAccessKeyPlaceholder')} value={settings.futuresKey || ''} onChange={onInputChange} />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-12 mb-3">
                                                    <div className="form-group">
                                                        <label htmlFor="secretKey">{t('settings.newFuturesSecretKey')}</label>
                                                        <input className="form-control" id="futuresSecret" type="password" autoComplete="new-password" placeholder={t('settings.newFuturesSecretKeyPlaceholder')} value={settings.futuresSecret || ''} onChange={onInputChange} />
                                                    </div>
                                                </div>
                                            </div>
                                        </React.Fragment>
                                    )
                                    : <React.Fragment></React.Fragment>
                            }
                            <div className="d-flex flex-wrap">
                                <button className="btn btn-gray-800 mt-2 animate-up-2" type="button" onClick={(e) => onFormSubmit(e, 'keys')}>{t('settings.save')}</button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12">
                        <div className="card card-body border-0 shadow mb-4">
                            <h2 className="h5 my-4">{t('settings.ai')}</h2>
                            <div className="gh-grid mb-3">
                                <div>
                                    <div className="form-group">
                                        <label htmlFor="aiProvider">{t('settings.aiProvider')}</label>
                                        <select className="form-select" id="aiProvider" value={settings.aiProvider || 'CLAUDE'} onChange={onInputChange}>
                                            <option value="CLAUDE">Claude (Anthropic)</option>
                                            <option value="OPENAI">OpenAI</option>
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <div className="form-group">
                                        <label htmlFor="aiApiKey">{t('settings.aiApiKey')}</label>
                                        <input className="form-control" id="aiApiKey" type="password" autoComplete="new-password" placeholder={t('settings.aiApiKeyPlaceholder')} value={settings.aiApiKey || ''} onChange={onInputChange} />
                                    </div>
                                </div>
                                <div>
                                    <div className="form-group">
                                        <label htmlFor="aiMaxDailySpendUsd">{t('settings.aiMaxDailySpend')}</label>
                                        <input className="form-control" id="aiMaxDailySpendUsd" type="number" step="0.50" min="0.50" max="100" placeholder="1.00" value={settings.aiMaxDailySpendUsd || ''} onChange={onInputChange} />
                                    </div>
                                </div>
                            </div>
                            <div className="d-flex flex-wrap">
                                <button className="btn btn-gray-800 mt-2 animate-up-2" type="button" onClick={onFormSubmit}>{t('settings.save')}</button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12 col-xl-8">
                        <TwoFactorCard />
                    </div>
                </div>
                <Footer />
            </main>
            <TwoFactorPrompt
                open={twoFaOpen}
                error={twoFaError}
                onSubmit={(code) => saveSettings(code)}
                onCancel={() => { setTwoFaOpen(false); setTwoFaError(''); }}
            />
            <Toast text={notification.text} type={notification.type} />
        </React.Fragment >
    );
}

export default Settings;
